import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { 
  Plus, Search, User, Mail, Phone, Building2, 
  CheckSquare, MessageSquare, Settings, Trash2, 
  UserCheck, X, Loader2, Eye
} from 'lucide-react';

function ClientsPage() {
  var authContext = useAuth();
  var api = authContext.api;
  var navigate = useNavigate();
  
  var stateHooks = {
    clients: useState([]),
    loading: useState(true),
    searchTerm: useState(''),
    showAddModal: useState(false),
    showImpersonateModal: useState(null)
  };

  var clients = stateHooks.clients[0];
  var setClients = stateHooks.clients[1];
  var loading = stateHooks.loading[0];
  var setLoading = stateHooks.loading[1];
  var searchTerm = stateHooks.searchTerm[0];
  var setSearchTerm = stateHooks.searchTerm[1];
  var showAddModal = stateHooks.showAddModal[0];
  var setShowAddModal = stateHooks.showAddModal[1];
  var showImpersonateModal = stateHooks.showImpersonateModal[0];
  var setShowImpersonateModal = stateHooks.showImpersonateModal[1];

  useEffect(function() {
    loadClients();
  }, []);

  function loadClients() {
    setLoading(true);
    api.get('/api/clients').then(function(res) {
      setClients(res.data.clients || []);
      setLoading(false);
    }).catch(function() {
      setLoading(false);
    });
  }

  function getFilteredClients() {
    if (!searchTerm) return clients;
    var term = searchTerm.toLowerCase();
    var filtered = [];
    for (var i = 0; i < clients.length; i++) {
      var c = clients[i];
      if ((c.name && c.name.toLowerCase().indexOf(term) !== -1) ||
          (c.email && c.email.toLowerCase().indexOf(term) !== -1) ||
          (c.company && c.company.toLowerCase().indexOf(term) !== -1)) {
        filtered.push(c);
      }
    }
    return filtered;
  }

  function getStatusColor(status) {
    var colors = {
      'qualified': 'bg-green-100 text-green-700 border-green-300',
      'new': 'bg-blue-100 text-blue-700 border-blue-300',
      'negotiation': 'bg-purple-100 text-purple-700 border-purple-300',
      'seller': 'bg-orange-100 text-orange-700 border-orange-300',
      'active': 'bg-green-100 text-green-700 border-green-300',
      'inactive': 'bg-gray-100 text-gray-700 border-gray-300',
      'pending': 'bg-yellow-100 text-yellow-700 border-yellow-300'
    };
    return colors[status] || colors['new'];
  }

  function handleDelete(clientId) {
    if (!window.confirm('Delete this client?')) return;
    api.delete('/api/clients/' + clientId).then(function() {
      toast.success('Client deleted');
      loadClients();
    }).catch(function() {
      toast.error('Failed to delete');
    });
  }

  function handleImpersonate(client) {
    setShowImpersonateModal(client);
  }

  function confirmImpersonate(client) {
    setShowImpersonateModal(null);
    api.post('/api/portal/admin-impersonate', { client_id: client.id })
      .then(function(res) {
        localStorage.setItem('portal_token', res.data.access_token);
        localStorage.setItem('portal_client', JSON.stringify(res.data.client));
        toast.success('Opening portal as ' + (client.first_name || client.name || 'client'));
        window.open('/portal', '_blank');
      })
      .catch(function() {
        toast.error('Failed to impersonate client');
      });
  }

  function renderClientCard(client) {
    var tags = client.tags || [];
    
    return (
      <div 
        key={client.id} 
        className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow cursor-pointer"
        onClick={function() { navigate('/crm/clients/' + client.id); }}
      >
        <div className="flex items-start justify-between">
          {/* Left side - Avatar and Info */}
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-6 h-6 text-amber-600" />
            </div>
            
            {/* Info */}
            <div className="space-y-1">
              <h3 className="font-semibold text-gray-900 text-lg">{client.name}</h3>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Mail className="w-3.5 h-3.5" />
                  {client.email}
                </span>
                {client.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />
                    {client.phone}
                  </span>
                )}
              </div>
              {client.company && (
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  <Building2 className="w-3.5 h-3.5" />
                  {client.company}
                </div>
              )}
            </div>
          </div>
          
          {/* Right side - Tags and Actions */}
          <div className="flex items-center gap-3">
            {/* Status Tags */}
            <div className="flex flex-wrap gap-2">
              {client.status && (
                <span className={"px-3 py-1 rounded-md text-xs font-semibold uppercase border " + getStatusColor(client.status)}>
                  {client.status}
                </span>
              )}
              {renderTags(tags)}
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-1 ml-2" onClick={function(e) { e.stopPropagation(); }}>
              <button 
                onClick={function(e) { e.stopPropagation(); handleImpersonate(client); }}
                className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                title="Impersonate Client"
              >
                <UserCheck className="w-5 h-5" />
              </button>
              <button 
                onClick={function(e) { e.stopPropagation(); navigate('/crm/clients/' + client.id); }}
                className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                title="View Details"
              >
                <Eye className="w-5 h-5" />
              </button>
              <button 
                className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                title="Message"
              >
                <MessageSquare className="w-5 h-5" />
              </button>
              <button 
                className="p-2 text-gray-400 hover:bg-gray-100 rounded-lg transition-colors"
                title="Settings"
              >
                <Settings className="w-5 h-5" />
              </button>
              <button 
                onClick={function(e) { e.stopPropagation(); handleDelete(client.id); }}
                className="p-2 text-red-400 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderTags(tags) {
    var tagElements = [];
    var tagColors = {
      'seller': 'bg-orange-100 text-orange-700 border-orange-300',
      'buyer': 'bg-cyan-100 text-cyan-700 border-cyan-300',
      'vip': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'priority': 'bg-red-100 text-red-700 border-red-300'
    };
    
    for (var i = 0; i < tags.length; i++) {
      var tag = tags[i];
      var color = tagColors[tag.toLowerCase()] || 'bg-gray-100 text-gray-700 border-gray-300';
      tagElements.push(
        <span key={tag} className={"px-3 py-1 rounded-md text-xs font-semibold uppercase border " + color}>
          {tag}
        </span>
      );
    }
    return tagElements;
  }

  function renderClientList() {
    var filtered = getFilteredClients();
    var cards = [];
    for (var i = 0; i < filtered.length; i++) {
      cards.push(renderClientCard(filtered[i]));
    }
    return cards;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#014DB7]" />
      </div>
    );
  }

  var filteredClients = getFilteredClients();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contacts ({clients.length})</h1>
          <p className="text-gray-500">Click on a contact to view details</p>
        </div>
        <Button onClick={function() { setShowAddModal(true); }} className="bg-[#014DB7] hover:bg-[#003A8C]">
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search clients..."
          value={searchTerm}
          onChange={function(e) { setSearchTerm(e.target.value); }}
          className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
        />
      </div>

      {/* Client List */}
      <div className="space-y-4">
        {filteredClients.length > 0 ? renderClientList() : (
          <div className="text-center py-12 bg-white rounded-xl border">
            <User className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No clients found</p>
            <Button onClick={function() { setShowAddModal(true); }} className="mt-4 bg-[#014DB7]">
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Client
            </Button>
          </div>
        )}
      </div>

      {/* Add Client Modal */}
      {showAddModal && (
        <AddClientModal 
          onClose={function() { setShowAddModal(false); }}
          onSave={function(data) {
            api.post('/api/clients', data).then(function() {
              toast.success('Client added');
              setShowAddModal(false);
              loadClients();
            }).catch(function() {
              toast.error('Failed to add client');
            });
          }}
        />
      )}

      {/* Impersonate Modal */}
      {showImpersonateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={function() { setShowImpersonateModal(null); }}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4" onClick={function(e) { e.stopPropagation(); }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Impersonate Client</h3>
              <button onClick={function() { setShowImpersonateModal(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl mb-4">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="font-semibold">{showImpersonateModal.name}</p>
                <p className="text-sm text-gray-500">{showImpersonateModal.email}</p>
              </div>
            </div>
            
            <p className="text-gray-600 mb-6">
              You will view the portal as this client sees it. This is useful for troubleshooting and support.
            </p>
            
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={function() { setShowImpersonateModal(null); }}>
                Cancel
              </Button>
              <Button onClick={function() { confirmImpersonate(showImpersonateModal); }} className="bg-[#014DB7]">
                <UserCheck className="w-4 h-4 mr-2" />
                View as Client
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddClientModal(props) {
  var formState = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    status: 'new',
    tags: ''
  });
  var form = formState[0];
  var setForm = formState[1];

  function handleChange(field, value) {
    var updated = {};
    for (var key in form) { updated[key] = form[key]; }
    updated[field] = value;
    setForm(updated);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={props.onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4" onClick={function(e) { e.stopPropagation(); }}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold">Add New Client</h3>
          <button onClick={props.onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input 
              type="text" 
              value={form.name} 
              onChange={function(e) { handleChange('name', e.target.value); }}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
              placeholder="John Smith"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input 
              type="email" 
              value={form.email} 
              onChange={function(e) { handleChange('email', e.target.value); }}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
              placeholder="john@example.com"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input 
              type="tel" 
              value={form.phone} 
              onChange={function(e) { handleChange('phone', e.target.value); }}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
              placeholder="(555) 123-4567"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
            <input 
              type="text" 
              value={form.company} 
              onChange={function(e) { handleChange('company', e.target.value); }}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
              placeholder="Smith Realty"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select 
              value={form.status} 
              onChange={function(e) { handleChange('status', e.target.value); }}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
            >
              <option value="new">New</option>
              <option value="qualified">Qualified</option>
              <option value="negotiation">Negotiation</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma separated)</label>
            <input 
              type="text" 
              value={form.tags} 
              onChange={function(e) { handleChange('tags', e.target.value); }}
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#014DB7]/30"
              placeholder="seller, vip"
            />
          </div>
        </div>
        
        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={props.onClose}>Cancel</Button>
          <Button 
            onClick={function() { 
              var tags = form.tags ? form.tags.split(',').map(function(t) { return t.trim(); }) : [];
              props.onSave({
                name: form.name,
                email: form.email,
                phone: form.phone,
                company: form.company,
                status: form.status,
                tags: tags
              }); 
            }} 
            className="bg-[#014DB7]"
          >
            Add Client
          </Button>
        </div>
      </div>
    </div>
  );
}

export default ClientsPage;
